/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { userSessionClientsRefreshCIMD } from "../funcs/userSessionClientsRefreshCIMD.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { UserSessionClient } from "../models/components/usersessionclient.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RefreshUserSessionClientCIMDRequest,
  RefreshUserSessionClientCIMDSecurity,
} from "../models/operations/refreshusersessionclientcimd.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RefreshUserSessionClientCIMDMutationVariables = {
  request: RefreshUserSessionClientCIMDRequest;
  security?: RefreshUserSessionClientCIMDSecurity | undefined;
  options?: RequestOptions;
};

export type RefreshUserSessionClientCIMDMutationData = UserSessionClient;

export type RefreshUserSessionClientCIMDMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * refreshUserSessionClientCIMD userSessionClients
 *
 * @remarks
 * Force a CIMD client's metadata document to be re-read. Purges the stored cache state (expiry + ETag) before re-fetching, so the read is unconditional: a host answering 304 Not Modified cannot re-confirm the copy the operator is discarding. Rejected for DCR clients. Deliberately bypasses the document cache, so it carries a per-client cooldown: a refresh shortly after the last successful read returns rate_limit_exceeded.
 */
export function useRefreshUserSessionClientCIMDMutation(
  options?: MutationHookOptions<
    RefreshUserSessionClientCIMDMutationData,
    RefreshUserSessionClientCIMDMutationError,
    RefreshUserSessionClientCIMDMutationVariables
  >,
): UseMutationResult<
  RefreshUserSessionClientCIMDMutationData,
  RefreshUserSessionClientCIMDMutationError,
  RefreshUserSessionClientCIMDMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRefreshUserSessionClientCIMDMutation(client, options),
    ...options,
  });
}

export function mutationKeyRefreshUserSessionClientCIMD(): MutationKey {
  return ["@gram/client", "userSessionClients", "refreshCIMD"];
}

export function buildRefreshUserSessionClientCIMDMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RefreshUserSessionClientCIMDMutationVariables,
  ) => Promise<RefreshUserSessionClientCIMDMutationData>;
} {
  return {
    mutationKey: mutationKeyRefreshUserSessionClientCIMD(),
    mutationFn: function refreshUserSessionClientCIMDMutationFn({
      request,
      security,
      options,
    }): Promise<RefreshUserSessionClientCIMDMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(userSessionClientsRefreshCIMD(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
